import { Injectable } from '@angular/core';
import JSZip from 'jszip';
import { kml } from '@tmcw/togeojson';

@Injectable({ providedIn: 'root' })
export class KmzAGeoJSONService {
  // Caché in-memory del GeoJSON parseado, keyeada por capa (config.id de la
  // ConfigCapa, NO por URL: las signed URLs de GCS rotan entre polls, así que
  // la URL no es estable y tampoco aprovecha el HTTP cache del browser).
  // Guardamos la Promise: dos cargas concurrentes de la misma capa (ej.
  // toggle de vista mientras el KMZ todavía baja) comparten un solo fetch.
  // Sin esto cada visita a la ruta / cambio de vista / re-toggle repite
  // descarga + JSZip + DOMParser + togeojson en el main thread.
  private cache = new Map<string, Promise<GeoJSON.FeatureCollection>>();

  public cargar(
    url: string,
    cacheKey?: string,
  ): Promise<GeoJSON.FeatureCollection> {
    return this.conCache(cacheKey, () => this.descargarYParsear(url));
  }

  // GeoJSON pre-convertido por el backend en el upload (IKmz.urlGeoJson):
  // fetch + JSON.parse directo, sin JSZip/DOMParser/togeojson en el main
  // thread. Misma caché por capa que el camino KMZ.
  public cargarGeoJson(
    url: string,
    cacheKey?: string,
  ): Promise<GeoJSON.FeatureCollection> {
    return this.conCache(cacheKey, async () => {
      const respuesta = await fetch(url);
      if (!respuesta.ok) {
        throw new Error(`No se pudo descargar GeoJSON (${respuesta.status})`);
      }
      return (await respuesta.json()) as GeoJSON.FeatureCollection;
    });
  }

  private conCache(
    cacheKey: string | undefined,
    cargar: () => Promise<GeoJSON.FeatureCollection>,
  ): Promise<GeoJSON.FeatureCollection> {
    if (!cacheKey) return cargar();
    const hit = this.cache.get(cacheKey);
    if (hit) return hit;
    const promesa = cargar();
    this.cache.set(cacheKey, promesa);
    // Un fetch fallido (KMZ caído, 504) no debe quedar cacheado: el próximo
    // intento reintenta desde cero.
    promesa.catch(() => this.cache.delete(cacheKey));
    return promesa;
  }

  private async descargarYParsear(
    url: string,
  ): Promise<GeoJSON.FeatureCollection> {
    const respuesta = await fetch(url);
    if (!respuesta.ok) {
      throw new Error(`No se pudo descargar KMZ (${respuesta.status})`);
    }
    const blob = await respuesta.blob();
    const zip = await JSZip.loadAsync(blob);
    const archivoKml = Object.values(zip.files).find((f) =>
      f.name.toLowerCase().endsWith('.kml'),
    );
    if (!archivoKml) {
      throw new Error('KMZ no contiene archivo .kml');
    }
    const texto = await archivoKml.async('string');
    const dom = new DOMParser().parseFromString(texto, 'text/xml');
    return kml(dom) as GeoJSON.FeatureCollection;
  }
}
